/**
 * Analytics dashboard API — the drill-down overview surface.
 * GET /api/analytics/overview is the single L0 round trip; drill views reuse
 * existing endpoints (debt-aging, debt-report, invoices, scanners).
 */
import { api } from './client';
import type { DebtAgingBucketDto, DebtAgingPersonDto } from './reports';

export interface AnalyticsReceivablesDto {
  totalOutstanding: number;
  overdueTotal: number;
  overdueSharePercent: number;
  personCount: number;
  buckets: DebtAgingBucketDto[];
  topDebtors: DebtAgingPersonDto[];
}

export interface AnalyticsPayablesDto {
  totalOutstanding: number;
  personCount: number;
  buckets: DebtAgingBucketDto[];
}

export interface CashAccountBalanceDto {
  accountNumber: number;
  accountName: string;
  balance: number;
}

export interface AnalyticsCashDto {
  total: number;
  source: 'bankAccounts' | 'heuristic271' | 'none';
  accounts: CashAccountBalanceDto[];
}

export interface AnalyticsVatDto {
  periodFrom: string;
  periodTo: string;
  salesVat: number;
  purchaseVat: number;
  netVatPayable: number;
}

export interface AnalyticsProblemEntryDto {
  id: string;
  timestamp: string;
  scanner: string;
  title: string;
  /** Raw scanner evidence JSON — contains drill ids (personId, invoiceId, journalEntryId). */
  evidence: string;
  confidence: number;
}

export interface AnalyticsProblemGroupDto {
  scanner: string;
  count: number;
  latestTimestamp: string | null;
  latestTitle: string | null;
}

export interface AnalyticsProblemsDto {
  lastRunUtc: string | null;
  totalFindings: number;
  groups: AnalyticsProblemGroupDto[];
  latest: AnalyticsProblemEntryDto[];
}

export interface AnalyticsFactDto {
  key: string;
  value: number;
  format: 'money' | 'percent' | 'count';
  targetView: string | null;
}

export interface AnalyticsOverviewDto {
  asOfDate: string;
  receivables: AnalyticsReceivablesDto;
  payables: AnalyticsPayablesDto;
  cash: AnalyticsCashDto;
  vat: AnalyticsVatDto;
  problems: AnalyticsProblemsDto;
  facts: AnalyticsFactDto[];
}

export interface DebtReportDto {
  person: {
    id: number;
    name: string;
    code: string;
    receivablesAccount: number;
    payablesAccount: number;
  };
  startBalance: number;
  debitTurnover: number;
  creditTurnover: number;
  endBalance: number;
}

export interface DecisionLogEntryDto {
  id: string;
  timestamp: string;
  agentName: string;
  authorityLevel: string;
  inputs: string;
  policyVersion: string;
  confidenceScore: number;
  evidence: string;
  outcome: string;
  isReversible: boolean;
  wasReverted: boolean;
  reviewerId: string | null;
  reviewedAt: string | null;
  tenantId: string;
}

export interface PaymentMatchDto {
  journalEntryId: number;
  documentNumber: string;
  date: string;
  amount: number;
  personId: number | null;
  personName: string | null;
  content: string | null;
  amountDifference: number;
}

export interface AiStatusDto {
  aiEnabled: boolean;
  workspaceId: string;
  lastSyncedAtUtc: string | null;
}

/** Extract known drill ids from a scanner finding's evidence JSON (defensive). */
export function parseEvidenceIds(evidence: string): { personId?: number; invoiceId?: number; journalEntryId?: number } {
  try {
    const raw = JSON.parse(evidence) as Record<string, unknown>;
    const num = (v: unknown): number | undefined =>
      typeof v === 'number' && Number.isFinite(v) ? v : undefined;
    return {
      personId: num(raw.personId),
      invoiceId: num(raw.invoiceId),
      journalEntryId: num(raw.journalEntryId),
    };
  } catch {
    return {};
  }
}

export const analyticsApi = {
  overview: () =>
    api.get<AnalyticsOverviewDto>('/analytics/overview'),

  debtReport: (personId: number, dateTo: string, type: 'receivable' | 'payable' = 'receivable') =>
    api.get<DebtReportDto>('/reports/debt-report', { personId, dateTo, type }),

  findings: (params: { scanner?: string; take?: number } = {}) =>
    api.get<{ items: DecisionLogEntryDto[]; totalCount: number }>('/scanners/results', {
      skip: 0,
      take: params.take ?? 100,
    }),

  findMatchingPayments: (invoiceJournalEntryId: number) =>
    api.post<PaymentMatchDto[]>('/scanners/find-matching-payments', { invoiceJournalEntryId }),

  aiStatus: (workspaceId: string) =>
    api.get<AiStatusDto>(`/workspaces/${workspaceId}/ai-status`),
};
